<template>
    <div class="address-autocomplete" :class="isActive || searchKey || autoCompleteText ? 'is-active' : ''">
        <form-group name="address" :label="label">
            <md-input name="address" v-model="autoCompleteText" type="hidden" />
            <vue-select
                class="address-select"
                :label="label"
                :id="id"
                :options="addressOptions"
                :reduce="(option) => option.value"
                :filterable="false"
                :loading="searchStatus === 'searching'"
                @search="handleSearch"
                @search:blur="handleBlur"
                @search:focus="handleFocus"
                @input="handleInput"
                v-model="selectedOption"
            >
                <template #search="{ attributes, events }">
                    <input
                        v-bind="{ ...attributes, autocomplete: autoCompleteText, value: searchKey }"
                        v-on="events"
                        type="search"
                        class="vs__search"
                        style="margin-left: -7px; font-size: 0.8rem"
                    />
                </template>
                <template v-slot:option="option">
                    <div class="address-option" :class="getClass(option)">
                        <template v-if="option.level === 0">
                            {{ option.label }}
                        </template>
                        <template v-else>
                            <div class="option-icon--container" v-if="option.icon">
                                <img :src="option.icon" alt="Location Icon" class="option-icon" />
                            </div>
                            <HighlightedAddressText :highlights="option.highlightInfo" :text="option.label" />
                        </template>
                    </div>
                </template>
                <template v-slot:no-options>
                    <template v-if="searchStatus === 'searching'">
                        <em>Searching</em>
                    </template>
                    <template v-if="searchStatus === 'idle'">
                        <em>Type an address to search</em>
                    </template>
                    <template v-if="searchStatus === 'done'">
                        <em>No results found</em>
                    </template>
                </template>
            </vue-select>
        </form-group>
    </div>
</template>
<script>
import { computed, onMounted, ref, toRefs } from '@vue/composition-api';
import useGoogleMapsAutocomplete from '@/compostables/useGoogleMapsAutocomplete';
import useDebounce from '@/compostables/useDebounce';
import HighlightedAddressText from '@/components/Autocomplete/HighlightedAddressText';
import useFrequentAddressQuery from '@/compostables/useFrequentAddressQuery';
import calculateHighlightInfo from '@/utils/calculateHighlightInfo';

/**
 * Use this component to provide an address autocomplete feature.
 */
export default {
    name: 'AddressAutoComplete',
    components: {
        HighlightedAddressText
    },
    props: {
        id: {
            type: String,
            required: true
        },
        dataTestId: {
            type: String,
            default: 'address-autocomplete'
        },
        value: {
            type: Object,
            default: () => null
        },
        label: {
            type: String,
            default: 'Address'
        },
        biasLocation: {
            type: Object,
            default: () => ({
                center: {
                    lat: -33.8688,
                    lng: 151.2093
                },
                // 400km radius
                radius: 400000
            })
        }
    },
    watch: {
        // for some reason, vue/composition-api's watch isn't working properly, so I had to use options API watch
        'value.address': {
            handler(newValue) {
                this.autoCompleteText = newValue;
                this.searchKey = newValue;
            },
            immediate: true
        },
        selectedOption: {
            handler(newValue) {
                if (newValue === null) {
                    this.autoCompleteText = '';
                    this.searchKey = '';
                }
            }
        }
    },
    setup(props, { emit, refs }) {
        const autoCompleteText = ref(null);
        const isActive = ref(false);
        const selectedOption = ref(null);

        const { value: modelValue, name, label } = toRefs(props);
        const customerId = computed(() => {
            return modelValue.value.customerId;
        });

        const biasLocation = computed(() => {
            return props.biasLocation;
        });

        const searchKey = ref('');

        const debouncedSearchKey = useDebounce(searchKey, 500);
        const {
            places,
            searchStatus: googleMapsSearchStatus,
            getPlaceDetails
        } = useGoogleMapsAutocomplete({
            searchKey: debouncedSearchKey,
            onGoogleMapsLoadSuccess: () => {
                if (modelValue !== null) {
                    autoCompleteText.value = modelValue.value.address;
                    searchKey.value = modelValue.value.address;
                }
            },
            biasLocation
        });
        const {
            searchStatus: frequentAddressSearchStatus,
            frequentAddresses,
            getSingleFrequentAddress
        } = useFrequentAddressQuery({
            searchKey: debouncedSearchKey,
            customerId
        });

        onMounted(() => {
            autoCompleteText.value = modelValue.value.address;
            searchKey.value = modelValue.value.address;
        });

        const searchStatus = computed(() => {
            if (googleMapsSearchStatus.value === 'searching' || frequentAddressSearchStatus.value === 'searching') {
                return 'searching';
            }
            if (googleMapsSearchStatus.value === 'done' && frequentAddressSearchStatus.value === 'done') {
                return 'done';
            }
            return 'idle';
        });
        /**
         * The list of address options to display in the autocomplete dropdown.
         * @type {ComputedRef<{ label: string, value: any, level: number, selectable: false, typography: "header" | "paragraph" }[]>}
         */
        const addressOptions = computed(() => {
            if (searchStatus.value === 'idle') {
                return [];
            }
            let mappedGoogleResult = [];
            if (places.value.length > 0) {
                mappedGoogleResult = places.value.map((place) => {
                    return {
                        label: place.description,
                        value: `place:${place.placeId}`,
                        highlightInfo: place.matchedSubstrings,
                        level: 1,
                        selectable: true,
                        typography: 'paragraph',
                        icon: '/img/icons/location-icon.svg'
                    };
                });
            } else {
                mappedGoogleResult = [
                    {
                        label: 'No search results found',
                        value: null,
                        level: 1,
                        selectable: false,
                        typography: 'paragraph',
                        highlightInfo: []
                    }
                ];
            }
            let frequentAddressResults = [];
            if (frequentAddresses.value.length > 0) {
                frequentAddressResults = frequentAddresses.value.map((address) => {
                    let label = address.address;
                    if (address.addressCode) {
                        label = `${address.contactName ? `${address.contactName} - ` : ''}${address.address} (${address.addressCode})`;
                    }
                    const highlightInfo = calculateHighlightInfo(label, debouncedSearchKey.value);
                    return {
                        label,
                        value: `frequentAddress:${address.frequentAddressId}`,
                        highlightInfo,
                        level: 1,
                        selectable: true,
                        typography: 'paragraph',
                        icon: '/img/icons/star-icon.svg'
                    };
                });
            } else {
                frequentAddressResults = [
                    {
                        label: 'No frequent addresses found',
                        value: null,
                        level: 1,
                        selectable: false,
                        typography: 'paragraph',
                        highlightInfo: []
                    }
                ];
            }
            const options = [
                {
                    label: 'Frequent Addresses',
                    value: null,
                    level: 0,
                    selectable: false,
                    typography: 'header'
                },
                ...frequentAddressResults,
                {
                    label: 'Search Results',
                    value: null,
                    level: 0,
                    selectable: false,
                    typography: 'header'
                },
                ...mappedGoogleResult
            ];

            return options;
        });

        function clearInternalStateSelectedOptions() {
            // refs.autoCompleteRef._value = [];
            selectedOption.value = null;
        }

        /**
         * Used to handle the input event.
         * @param {string} value - if in the format `place:place_id`, it means that the user selected a place from the dropdown.
         * Otherwise, it is the search key.
         */
        function handleInput(value) {
            /**
             * usually it's null if the user clears it
             */
            if (value === null) {
                autoCompleteText.value = '';
                searchKey.value = '';
                // reset
                const value = Object.assign({}, modelValue);
                value.location = null;
                value.address = null;
                value.name = null;
                value.addressComponents = null;

                emit('placechanged', {
                    location: null,
                    address: null,
                    name: null,
                    addressComponents: null
                });

                emit('change', autoCompleteText);
            } else if (value.startsWith('place:')) {
                // The user selected a place from the dropdown
                const placeId = value.replace('place:', '');
                getPlaceDetails(placeId).then((placeResult) => {
                    const { location, name, address, addressComponents } = placeResult.data;
                    const value = Object.assign({}, modelValue);
                    autoCompleteText.value = address;
                    searchKey.value = address;
                    value.location = location;
                    value.address = address;
                    value.name = name;
                    value.addressComponents = addressComponents;
                    emit('placechanged', placeResult.data);
                    emit('change', autoCompleteText);
                });
            } else if (value.startsWith('frequentAddress:')) {
                const frequentAddressId = parseInt(value.replace('frequentAddress:', ''), 10);
                getSingleFrequentAddress(frequentAddressId).then(
                    /**
                     *
                     * @param {import("@/jsDocTypes/FrequentAddress").FrequentAddress} response
                     */
                    (response) => {
                        const { address, addressComponents } = response;
                        const { location } = addressComponents;
                        autoCompleteText.value = address;
                        searchKey.value = address;
                        emit('placechanged', {
                            location,
                            address,
                            name: address,
                            addressComponents
                        });
                        emit('frequentAddressSelected', response);
                        emit('change', autoCompleteText);
                    }
                );
            } else {
                // The user is still typing
                autoCompleteText.value = value;
            }
        }

        /**
         * Handle the search event.
         * @param {string} searchQuery The search key.
         * @param {(status: boolean) => void} loading The loading callback, use this to indicate that the search is in progress.
         */
        function handleSearch(searchQuery, loading) {
            if (!searchQuery.length) {
                return;
            }
            clearInternalStateSelectedOptions();
            searchKey.value = searchQuery;
        }

        function handleBlur() {
            autoCompleteText.value = modelValue?.value.address || '';
            isActive.value = false;
        }

        function handleFocus() {
            isActive.value = true;
        }

        function getClass(option) {
            const classes = [];
            classes.push(`option-level-${option.level}`);
            classes.push(`option-text-${option.typography}`);
            return classes.join(' ');
        }

        /**
         * Does a breadth-first search to find the child element with the given class name.
         * @param {HTMLElement} parentElement
         * @param {string} className
         */
        function findChildElement(parentElement, className) {
            const { children } = parentElement;
            const queue = [...children];
            while (queue.length > 0) {
                const currentElement = queue.shift();
                if (currentElement.classList.contains(className)) {
                    return currentElement;
                }
                const currentElementChildren = currentElement.children;
                for (let i = 0; i < currentElementChildren.length; i++) {
                    queue.push(currentElementChildren[i]);
                }
            }
            return null;
        }

        onMounted(() => {
            const { dataTestId } = props;
            if (!dataTestId) {
                return;
            }
            const dropdownElement = document.getElementById(props.id);
            if (!dropdownElement) {
                return;
            }
            const inputField = findChildElement(dropdownElement, 'vs__search');
            if (inputField) {
                inputField.setAttribute('data-testid', dataTestId);
            }
        });

        return {
            selectedOption,
            isActive,
            name,
            label,
            searchKey,
            autoCompleteText,
            addressOptions,
            searchStatus,
            handleSearch,
            getClass,
            handleInput,
            handleBlur,
            handleFocus,
            locationIcon: '/img/icons/location-icon.svg',
            starIcon: '/img/icons/star-icon.svg'
        };
    }
};
</script>
<style scoped lang="scss">
.address-autocomplete {
    &.is-active {
        ::v-deep .md-field.md-theme-default label {
            top: -0.8rem !important;
            font-size: 0.6875rem !important;
        }
    }
}
.option-text {
    &-header {
        font-weight: bold;
    }

    &-paragraph {
        font-weight: normal;
    }
}

.option-level {
    &-0 {
    }

    &-1 {
        padding-left: 5px;
    }
}

.address-option {
    display: grid;
    grid-template-columns: 24px 1fr 24px;
    align-items: center;
    justify-items: start;
    gap: 2.5px;
}

::v-deep .vs__dropdown-option {
    padding: 2px;
    font-size: 14px;
}

::v-deep .vs__dropdown-menu {
    overflow-y: hidden;
    padding: 0 4px;
}

::v-deep .vs__selected {
    display: none;
}

::v-deep .vs__selected-options {
    white-space: nowrap;
    flex-wrap: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.option-icon--container {
    width: 16px;
    height: 16px;
    transform: translateY(-25%);
}

.option-icon {
    width: 100%;
    height: 100%;
}
</style>
